import React from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { alpha } from "@mui/material/styles";

const YesNoSwitch = ({
  disabled = false,
  noLabel = "No",
  onChange,
  size = "small",
  sx,
  value,
  yesLabel = "Yes",
}) => {
  const selectedValue = value ? "yes" : "no";
  const selectedColor = value ? "success" : "error";
  const unselectedColor = value ? "error" : "success";

  return (
    <ToggleButtonGroup
      exclusive
      size={size}
      value={selectedValue}
      onChange={(_, nextValue) => {
        if (!nextValue) return;
        onChange?.(nextValue === "yes");
      }}
      disabled={disabled}
      sx={[
        {
          position: "relative",
          display: "inline-grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          isolation: "isolate",
          overflow: "hidden",
          transition: "transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          "&:active": {
            transform: disabled ? "none" : "scale(0.97, 0.94)",
            transition: "transform 90ms ease-out",
          },
          // One shared background keeps the selection moving in both directions.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            width: "50%",
            bgcolor: `${selectedColor}.main`,
            transform: `translateX(${value ? "0%" : "100%"})`,
            transition:
              "transform 340ms cubic-bezier(0.34, 1.35, 0.64, 1), background-color 200ms ease",
            pointerEvents: "none",
            zIndex: 0,
          },
          "& .MuiToggleButton-root": {
            zIndex: 1,
            minWidth: 0,
            px: 2,
            textTransform: "none",
            fontWeight: 600,
            transition: "color 180ms ease, background-color 180ms ease",
          },
          "& .MuiToggleButtonGroup-grouped": {
            margin: 0,
          },
          "& .MuiToggleButton-root.Mui-selected": {
            bgcolor: "transparent",
            color: `${selectedColor}.contrastText`,
            "&:hover": {
              bgcolor: "transparent",
            },
          },
          "@media (hover: hover)": {
            "& .MuiToggleButton-root.Mui-selected:not(.Mui-disabled):hover": {
              bgcolor: alpha("#fff", 0.12),
            },
            "& .MuiToggleButton-root:not(.Mui-selected):not(.Mui-disabled):hover": {
              bgcolor: (theme) =>
                alpha(theme.palette[unselectedColor].main, 0.1),
              color: `${unselectedColor}.dark`,
            },
          },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&:active": {
              transform: "none",
              transition: "none",
            },
            "&::before, & .MuiToggleButton-root": {
              transition: "none",
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <ToggleButton value='yes'>{yesLabel}</ToggleButton>
      <ToggleButton value='no'>{noLabel}</ToggleButton>
    </ToggleButtonGroup>
  );
};

export default YesNoSwitch;
